import { useState, FormEvent, ChangeEvent } from 'react'
import { betaApiService, BetaSignupData, BetaError, DIETARY_PREFERENCES } from '@talktoast/shared'

interface BetaFormProps {
  onSuccess: (data: BetaSignupData & { position?: number }) => void
  onError?: (error: BetaError) => void
}

type FormState = 'idle' | 'submitting' | 'success' | 'error'

interface FormErrors {
  email?: string
  name?: string
  householdSize?: string
  general?: string
}

// Use shared dietary preferences constant (includes all 10 options)
// Add "No restrictions" option for beta signup form
const BETA_DIETARY_PREFERENCES = [
  ...DIETARY_PREFERENCES,
  { value: 'none', label: 'No restrictions', description: 'No dietary restrictions' }
]

const GROCERY_STORES = [
  'Kroger',
  'Safeway',
  'Whole Foods',
  'Trader Joe\'s',
  'Albertsons',
  'Publix',
  'HEB',
  'Wegmans',
  'Aldi',
  'Costco',
  'Target',
  'Walmart',
  'Other',
]

const REFERRAL_SOURCES = [
  'Social Media',
  'Friend/Family',
  'Search Engine',
  'Blog/Article',
  'Advertisement',
  'Other',
]

export default function BetaForm({ onSuccess, onError }: BetaFormProps) {
  const [formState, setFormState] = useState<FormState>('idle')
  const [formData, setFormData] = useState<BetaSignupData>({
    email: '',
    name: '',
    dietaryPreferences: [],
    householdSize: undefined,
    primaryGroceryStore: '',
    referralSource: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({})

  // Email validation
  const validateEmail = (email: string): string | undefined => {
    if (!email) {
      return 'Email is required'
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address'
    }
    return undefined
  }

  // Name validation
  const validateName = (name: string): string | undefined => {
    if (!name) {
      return 'Name is required'
    }
    if (name.length < 2) {
      return 'Name must be at least 2 characters'
    }
    return undefined
  }

  // Household size validation
  const validateHouseholdSize = (size: number | undefined): string | undefined => {
    if (size !== undefined && (size < 1 || size > 20)) {
      return 'Household size must be between 1 and 20'
    }
    return undefined
  }

  // Handle input change
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'householdSize' && value ? parseInt(value, 10) : value,
    }))

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  // Handle checkbox change for dietary preferences
  const handleDietaryChange = (value: string) => {
    setFormData((prev) => {
      const currentPrefs = prev.dietaryPreferences || []

      // If "none" is selected, clear all other selections
      if (value === 'none') {
        return { ...prev, dietaryPreferences: ['none'] }
      }

      // If another option is selected, remove "none"
      const withoutNone = currentPrefs.filter((pref) => pref !== 'none')

      if (currentPrefs.includes(value)) {
        return {
          ...prev,
          dietaryPreferences: withoutNone.filter((pref) => pref !== value),
        }
      }

      return { ...prev, dietaryPreferences: [...withoutNone, value] }
    })
  }

  // Handle blur event to mark field as touched
  const handleBlur = (fieldName: string) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))

    // Validate on blur
    let error: string | undefined
    switch (fieldName) {
      case 'email':
        error = validateEmail(formData.email)
        break
      case 'name':
        error = validateName(formData.name)
        break
      case 'householdSize':
        error = validateHouseholdSize(formData.householdSize)
        break
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [fieldName]: error }))
    }
  }

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    // Mark all required fields as touched
    setTouched({
      email: true,
      name: true,
      householdSize: true,
    })

    // Validate all fields
    const emailError = validateEmail(formData.email)
    const nameError = validateName(formData.name)
    const householdSizeError = validateHouseholdSize(formData.householdSize)

    if (emailError || nameError || householdSizeError) {
      setErrors({
        email: emailError,
        name: nameError,
        householdSize: householdSizeError,
      })
      return
    }

    // Submit form
    setFormState('submitting')
    setErrors({})

    try {
      // Prepare data for submission (remove empty optional fields)
      const submitData: BetaSignupData = {
        email: formData.email,
        name: formData.name,
      }

      if (formData.dietaryPreferences && formData.dietaryPreferences.length > 0) {
        submitData.dietaryPreferences = formData.dietaryPreferences
      }
      if (formData.householdSize) {
        submitData.householdSize = formData.householdSize
      }
      if (formData.primaryGroceryStore) {
        submitData.primaryGroceryStore = formData.primaryGroceryStore
      }
      if (formData.referralSource) {
        submitData.referralSource = formData.referralSource
      }

      const response = await betaApiService.submitBetaSignup(submitData)

      setFormState('success')
      onSuccess({
        ...submitData,
        position: response.data?.position,
      })
    } catch (error: any) {
      setFormState('error')

      // Handle specific error types
      if (error.code === 'DUPLICATE_EMAIL') {
        setErrors({ email: 'This email is already on the waitlist' })
      } else if (error.code === 'VALIDATION_ERROR') {
        setErrors({ general: error.message })
      } else {
        setErrors({
          general: error.message || 'Something went wrong. Please try again.'
        })
      }

      if (onError) {
        onError(error)
      }
    }
  }

  const isSubmitting = formState === 'submitting'

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={() => handleBlur('email')}
          disabled={isSubmitting}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${
            errors.email && touched.email
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300'
          } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="your@email.com"
        />
        {errors.email && touched.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
        )}
      </div>

      {/* Name Field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          onBlur={() => handleBlur('name')}
          disabled={isSubmitting}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${
            errors.name && touched.name
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300'
          } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="John Doe"
        />
        {errors.name && touched.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Dietary Preferences */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dietary Preferences <span className="text-gray-500">(Optional)</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {BETA_DIETARY_PREFERENCES.map((pref) => (
            <label
              key={pref.value}
              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                formData.dietaryPreferences?.includes(pref.value)
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-300 hover:border-orange-300'
              } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={formData.dietaryPreferences?.includes(pref.value)}
                onChange={() => handleDietaryChange(pref.value)}
                disabled={isSubmitting}
                className="sr-only"
              />
              <span className="text-sm font-medium">{pref.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Household Size */}
      <div>
        <label htmlFor="householdSize" className="block text-sm font-medium text-gray-700 mb-1">
          Household Size <span className="text-gray-500">(Optional)</span>
        </label>
        <input
          type="number"
          id="householdSize"
          name="householdSize"
          min="1"
          max="20"
          value={formData.householdSize || ''}
          onChange={handleChange}
          onBlur={() => handleBlur('householdSize')}
          disabled={isSubmitting}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${
            errors.householdSize && touched.householdSize
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300'
          } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="How many people do you cook for?"
        />
        {errors.householdSize && touched.householdSize && (
          <p className="mt-1 text-sm text-red-600">{errors.householdSize}</p>
        )}
      </div>

      {/* Primary Grocery Store */}
      <div>
        <label htmlFor="primaryGroceryStore" className="block text-sm font-medium text-gray-700 mb-1">
          Primary Grocery Store <span className="text-gray-500">(Optional)</span>
        </label>
        <select
          id="primaryGroceryStore"
          name="primaryGroceryStore"
          value={formData.primaryGroceryStore || ''}
          onChange={handleChange}
          disabled={isSubmitting}
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${
            isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
        >
          <option value="">Select a store...</option>
          {GROCERY_STORES.map((store) => (
            <option key={store} value={store}>
              {store}
            </option>
          ))}
        </select>
      </div>

      {/* Referral Source */}
      <div>
        <label htmlFor="referralSource" className="block text-sm font-medium text-gray-700 mb-1">
          How did you hear about us? <span className="text-gray-500">(Optional)</span>
        </label>
        <select
          id="referralSource"
          name="referralSource"
          value={formData.referralSource || ''}
          onChange={handleChange}
          disabled={isSubmitting}
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${
            isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
        >
          <option value="">Select an option...</option>
          {REFERRAL_SOURCES.map((source) => (
            <option key={source} value={source}>
              {source}
            </option>
          ))}
        </select>
      </div>

      {/* General Error Message */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{errors.general}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all ${
          isSubmitting
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 transform hover:scale-105'
        }`}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Joining Waitlist...
          </span>
        ) : (
          'Join the Waitlist'
        )}
      </button>

      {/* Privacy Notice */}
      <p className="text-xs text-center text-gray-500">
        By signing up, you agree to receive updates about TalkToast.
        <br />
        We respect your privacy and you can unsubscribe at any time.
      </p>
    </form>
  )
}
